import {
    AdditionalProductFragment,
    VariantFragment,
} from '@graphql/generated/graphql';
import {
    isVariantDeleted,
    isVariantInStock,
    isVariantOutOfStock,
} from '@utils/variant';
import { useState, useCallback } from 'react';

export type Select = {
    name: string;
    options: { value: string; disabled?: boolean }[];
    value?: string;
};

interface UseSelectsReturn {
    additionalProduct: AdditionalProductFragment | null;
    selectedVariant: VariantFragment | null;
    selects: Select[];
    onSelectOption: (optionName: string, value?: string) => void;
}

interface Props {
    additionalProduct: AdditionalProductFragment | null;
    defaultSelectedVariant?: VariantFragment | null;
}

export const useVariantSelects = ({
    additionalProduct,
    defaultSelectedVariant,
}: Props): UseSelectsReturn => {
    const existingVariants = (additionalProduct?.Variants || []).filter(
        (v) => !isVariantDeleted(v)
    );

    const getOptionMap = useCallback((variants: VariantFragment[]) => {
        const optionMap: Record<string, Set<string>> = {};
        // Build list of options and unique values
        for (const variant of variants) {
            for (const opt of variant.Options ?? []) {
                if (!optionMap[opt.name]) optionMap[opt.name] = new Set();
                optionMap[opt.name].add(opt.value);
            }
        }
        return optionMap;
    }, []);

    const buildSelects = useCallback(
        (
            variants: VariantFragment[],
            selectedVariant?: VariantFragment | null
        ) => {
            // Collect all possible options for each name
            const optionMap = getOptionMap(variants);

            // Build list of selects
            return Object.keys(optionMap).map((name) => ({
                name,
                options: Array.from(optionMap[name]).map((o) => ({
                    value: o,
                    disabled: false,
                })),
                value: selectedVariant?.Options?.find(
                    (opt) => opt.name === name
                )?.value,
            }));
        },
        [getOptionMap]
    );

    const getInitialSelectedVariant = useCallback(() => {
        if (
            defaultSelectedVariant &&
            isVariantInStock(defaultSelectedVariant)
        ) {
            return defaultSelectedVariant;
        }

        if (
            existingVariants.length === 1 &&
            isVariantInStock(existingVariants[0])
        ) {
            return existingVariants[0];
        }

        return null;
    }, [defaultSelectedVariant, existingVariants]);

    const [selectedVariant, setSelectedVariant] =
        useState<VariantFragment | null>(getInitialSelectedVariant());

    const filterVariants = useCallback(
        (variants: VariantFragment[], selects: Select[]) => {
            return variants.filter((variant) => {
                return selects.every((select) => {
                    if (!select.value) return true;
                    return variant.Options?.some(
                        (option) =>
                            option.name === select.name &&
                            option.value === select.value
                    );
                });
            });
        },
        []
    );

    const syncSelectsValueWithVariant = useCallback(
        (selects: Select[], variant: VariantFragment): Select[] => {
            return selects.map((select) => ({
                ...select,
                value: variant.Options?.find((o) => o.name === select.name)
                    ?.value,
            }));
        },
        []
    );

    const updateSelectsDisabled = useCallback(
        (variants: VariantFragment[], selects: Select[]) => {
            return selects.map((select) => {
                return {
                    ...select,
                    options: select.options.map((option) => {
                        const tempSelection = selects.map((s) =>
                            s.name === select.name
                                ? { name: s.name, value: option.value }
                                : s
                        );
                        const matchingVariants = variants.filter((variant) =>
                            tempSelection.every((sel) => {
                                if (!sel.value) return true;
                                return variant.Options?.some(
                                    (opt) =>
                                        opt.name === sel.name &&
                                        opt.value === sel.value
                                );
                            })
                        );
                        const disabled =
                            matchingVariants.length < 1 ||
                            matchingVariants.every(isVariantOutOfStock);
                        return { ...option, disabled };
                    }),
                    value: select.value,
                };
            });
        },
        []
    );

    const [selects, setSelects] = useState<Select[]>(() => {
        const selects = buildSelects(
            existingVariants,
            getInitialSelectedVariant()
        );
        return updateSelectsDisabled(existingVariants, selects);
    });

    const onSelectOption = useCallback(
        (optionName: string, value?: string) => {
            let updatedSelects = selects.map((select) =>
                select.name === optionName ? { ...select, value } : select
            );
            let filteredVariants = filterVariants(
                existingVariants,
                updatedSelects
            );

            let newSelectedVariant: VariantFragment | null = null;

            if (
                filteredVariants.length === 1 &&
                filteredVariants[0].id !== selectedVariant?.id
            ) {
                newSelectedVariant = filteredVariants[0];
                updatedSelects = syncSelectsValueWithVariant(
                    selects,
                    newSelectedVariant
                );
                filteredVariants = filterVariants(
                    existingVariants,
                    updatedSelects
                );
            }

            setSelectedVariant(newSelectedVariant);
            setSelects(updateSelectsDisabled(existingVariants, updatedSelects));
        },
        [
            existingVariants,
            selectedVariant,
            selects,
            filterVariants,
            syncSelectsValueWithVariant,
            updateSelectsDisabled,
        ]
    );

    return {
        additionalProduct,
        selectedVariant,
        selects,
        onSelectOption,
    };
};
